import { ApiError } from "@altek/ui/lib/problem";

/**
 * The bytes of an upload, sent straight to object storage.
 *
 * THIS IS THE ONE REQUEST THAT DOES NOT GO THROUGH `client.ts`, and the reason is the whole
 * design of the file module: a presigned `PUT` goes to the bucket, not to the API. The server
 * never sees the bytes — it issues the URL, records the row, and authorises the read later. A
 * 50 MB scan streamed through Node would hold an event-loop worker and a request slot for the
 * whole transfer; four concurrent ones would starve every other request on the instance.
 *
 * XHR RATHER THAN FETCH, for exactly one reason: fetch cannot report upload progress. A 40 MB
 * file going up behind an indeterminate spinner is indistinguishable from a frozen tab, and the
 * person's only move is to reload — which loses the upload and starts it again.
 *
 * CREDENTIALS ARE OMITTED. The signature in the URL IS the grant; a session cookie would add
 * nothing except a request the CSRF guard has to reason about. Cross-origin (S3/MinIO) it would
 * also fail the preflight outright.
 *
 * WHAT IT NEVER DOES: retry. A half-sent 50 MB body cannot be resumed from here, and a silent
 * second attempt doubles the transfer on a connection that is already struggling. The caller
 * decides, with the user watching.
 */

export interface PresignedUpload {
  /** Where the bytes go. Absolute for S3/MinIO, app-relative for the local storage driver. */
  readonly url: string;
  readonly method?: "PUT";
  /**
   * Headers the store signed and therefore REQUIRES back, byte for byte. `Content-Type` is the
   * usual one: S3 refuses the upload when it differs from the signature.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

export interface UploadOptions {
  /** 0–100. Determinate on purpose: an indeterminate bar cannot answer "how much longer". */
  onProgress?: ((percent: number) => void) | undefined;
  signal?: AbortSignal | undefined;
}

/**
 * Headers a browser refuses to let script set. Sending them is not an error the store reports —
 * the XHR simply throws, or silently drops them — so they are skipped with the reason stated
 * rather than passed through and wondered about later.
 */
const FORBIDDEN_HEADERS = new Set([
  "content-length",
  "host",
  "connection",
  "origin",
  "referer",
]);

/** An app-relative URL means the local storage driver: same origin, and the CSRF guard applies. */
function isSameOrigin(url: string): boolean {
  if (url.startsWith("/")) return true;
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function apiUpload(
  intent: PresignedUpload,
  body: Blob,
  options: UploadOptions = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(intent.method ?? "PUT", intent.url, true);

    const sameOrigin = isSameOrigin(intent.url);
    // Same-origin means our own local-object route, which sits behind the CSRF guard like every
    // other write. Cross-origin means the bucket, which has never heard of our cookies.
    request.withCredentials = false;

    for (const [name, value] of Object.entries(intent.headers ?? {})) {
      if (FORBIDDEN_HEADERS.has(name.toLowerCase())) continue;
      request.setRequestHeader(name, value);
    }
    if (sameOrigin) {
      const csrf = readCookie("csrf");
      if (csrf) request.setRequestHeader("x-csrf-token", csrf);
    }

    if (options.onProgress) {
      const report = options.onProgress;
      request.upload.addEventListener("progress", (event) => {
        // `lengthComputable` is false for a chunked body; reporting 0 there would make the bar
        // jump backwards, so the caller keeps whatever it last showed.
        if (event.lengthComputable && event.total > 0) {
          report(Math.min(100, Math.round((event.loaded / event.total) * 100)));
        }
      });
    }

    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        options.onProgress?.(100);
        resolve();
        return;
      }
      reject(
        new ApiError({
          status: request.status,
          code: "UPLOAD_FAILED",
          serverMessage: "Dosya depoya yüklenemedi",
          requestId: request.getResponseHeader("x-request-id"),
        }),
      );
    });

    request.addEventListener("error", () => {
      // A network-level failure carries no status; reporting 0 as an HTTP code would be a lie.
      reject(
        new ApiError({
          status: 0,
          code: "NETWORK_ERROR",
          serverMessage: "Yükleme sırasında bağlantı koptu",
          requestId: null,
        }),
      );
    });

    request.addEventListener("abort", () => {
      reject(new DOMException("Yükleme iptal edildi", "AbortError"));
    });

    if (options.signal) {
      /**
       * An ALREADY-aborted signal is rejected here rather than passed to `abort()`.
       *
       * `XMLHttpRequest.abort()` fires its `abort` event only for a request that is in flight —
       * opened AND sent. Called before `send()`, as it would be here, it does nothing at all:
       * the listener above never runs, the promise never settles, and the caller waits forever
       * on an upload that was cancelled before it started. A component unmounting between
       * picking a file and sending it is enough to hit it.
       */
      if (options.signal.aborted) {
        reject(new DOMException("Yükleme iptal edildi", "AbortError"));
        return;
      }
      options.signal.addEventListener("abort", () => request.abort(), { once: true });
    }

    request.send(body);
  });
}
